import { useEffect } from 'react'
import { create } from 'zustand'
import { en, type StringKey } from './en'
import { es } from './es'

/**
 * The app's strings, per locale (Brian, docs/LOCALIZATION.md "Rules for the
 * build" 1 and DEVELOPER-HANDOFF "Languages and launch countries": "Every
 * screen's text in a strings file from day one, English and Spanish (US
 * Spanish, tú)"). v1 launches in the US in en-US and es-US only.
 *
 *   const t = useT();  t('voice.left', { n: 13, total: 18 })
 *
 * A key missing from a catalogue falls back to English, so a screen is never
 * blank while its Spanish waits. The Spanish is AI-drafted and unreviewed
 * (es.ts, REVIEWED = false) until a native US-Spanish reviewer signs it off.
 *
 * The locale: the account's language when the account row carries one
 * (clients.language: en, es or bilingual, which the onboarding language picker
 * writes; bilingual keeps the screens in English), else what
 * was set on this device (setLocale), else English. Never guessed from the
 * browser here: onboarding asks.
 *
 * Only the voice screens are in the catalogues so far (the ring, its sheet and
 * offer, the voice profile card, the onboarding voice questions' chrome);
 * every other screen still carries its English inline and moves here screen
 * by screen.
 */

export type Locale = 'en-US' | 'es-US'
export const LOCALES: Locale[] = ['en-US', 'es-US']
export type { StringKey }

const CATALOGUE: Record<Locale, Partial<Record<StringKey, string>>> = { 'en-US': en, 'es-US': es }
const KEY = 'gg.locale'

export function normalizeLocale(raw: string | null | undefined): Locale | null {
  if (!raw) return null
  const v = raw.toLowerCase()
  // Bilingual: the app's screens speak English, the scripts come in both (onboarding v2.12).
  if (v === 'bilingual') return 'en-US'
  if (v === 'es' || v.startsWith('es-') || v.startsWith('es_')) return 'es-US'
  if (v === 'en' || v.startsWith('en-') || v.startsWith('en_')) return 'en-US'
  return null
}

function stored(): Locale {
  try { return normalizeLocale(localStorage.getItem(KEY)) ?? 'en-US' } catch { return 'en-US' }
}

export const useLocaleStore = create<{ locale: Locale; set: (l: Locale) => void }>((set) => ({
  locale: stored(),
  set: (locale) => {
    try { localStorage.setItem(KEY, locale) } catch { /* private window */ }
    set({ locale })
  },
}))

/** Sets the app's language on this device (the onboarding picker, Settings). */
export function setLocale(l: Locale) { useLocaleStore.getState().set(l) }

export function translate(locale: Locale, key: StringKey, vars?: Record<string, string | number>): string {
  const raw = CATALOGUE[locale][key] ?? en[key] ?? key
  if (!vars) return raw
  return raw.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m))
}

export function useLocale(): Locale {
  return useLocaleStore((s) => s.locale)
}

export function useT() {
  const locale = useLocale()
  return (key: StringKey, vars?: Record<string, string | number>) => translate(locale, key, vars)
}

/**
 * Keeps the locale in step with the account's language (clients.language,
 * when the column exists) and puts it on <html lang>, so the browser
 * hyphenates long words in the right language (LOCALIZATION rule 6).
 * Mounted once, in the app shell.
 */
export function useLocaleSync(accountLanguage: string | null | undefined) {
  const locale = useLocale()
  useEffect(() => {
    const l = normalizeLocale(accountLanguage)
    if (l && l !== useLocaleStore.getState().locale) setLocale(l)
  }, [accountLanguage])
  useEffect(() => {
    document.documentElement.lang = locale === 'es-US' ? 'es-US' : 'en-US'
  }, [locale])
}
